import {
    ArrowBackIosRounded,
    Call,
    ContentCopy,
    MessageRounded,
    WhatsApp
} from "@mui/icons-material";
import { Avatar, Box, CircularProgress, FormControlLabel, IconButton } from "@mui/material";
import { getAuth } from "firebase/auth";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IOSSwitch } from "../components/CustomButtons";
import DeleteButton from "../components/deleteButton";
import AddAccountForm from "../components/forms/addAccountForm";
import EditCustomerForm from "../components/forms/editCustomerDetailsForm";
import { deleteAccount, deleteCustomer, getAccountsList, getcustomerDetails, getPreferences, setCustomerStatus } from "../firebase/dataHandler";
import { generateCustomerReport } from "../utils/whatsappGenerator";
import Accounts from "./accounts";
import { useTranslation } from "react-i18next";

function CustomerDetail() {
  const { t } = useTranslation();
  const { customerId } = useParams();
  const [user] = useState(getAuth().currentUser);
  const clientId = `${customerId}_${user.uid}`;
  const [data, setData] = useState(null);
  const [accountsList, setAccountsList] = useState([]);
  const navigate = useNavigate();
  const [useHandedAmount, setUseHandedAmount] = useState(false);

  useEffect(() => {
    getPreferences().then(prefs => {
      setUseHandedAmount(prefs.useHandedAmountForAnalytics || false);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    getCustomerDetails(clientId);
    getAccountsList().then(res => {
      if (res) {
        setAccountsList(Object.values(res).filter(a => a.clientId === clientId && (a.isActive || a.isActive === undefined)));
      }
    });
  }, [clientId]);

  const getCustomerDetails = (cid) => {
    getcustomerDetails(cid)
      .then((customer) => setData(customer))
      .catch((err) => alert(err));
  };

  const totalInvested = accountsList.reduce((acc, sum) => acc + (useHandedAmount
    ? Number(sum.amount || 0) - Number(sum.interestAmount || 0)
    : Number(sum.amount || 0)), 0);

  const totalPaid = accountsList
    .filter(a => a.entries)
    .map(({ entries }) => Object.values(entries))
    .flat()
    .reduce((acc, sum) => acc + (sum.isUpfrontInterest ? 0 : Number(sum.amountPaid)), 0);

  const activeBalance = accountsList.reduce((acc, sum) => {
    const paidForAccount = sum.entries
      ? Object.values(sum.entries).reduce((a, s) => a + (s.isUpfrontInterest ? 0 : Number(s.amountPaid)), 0) : 0;
    return acc + (Number(sum.amount) - paidForAccount);
  }, 0);

  // Public console URL — same pattern as existing CustomerConsole
  const consoleUrl = data?.clientId
    ? `https://finaculator.web.app/console/c/${data.clientId}`
    : null;

  if (!data) return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><CircularProgress /></div>;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* ── Sticky Top Bar ── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px 20px', borderBottom: '1px solid var(--border-subtle)',
        background: 'var(--bg-primary)', zIndex: 100, flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <IconButton onClick={() => navigate(-1)} size="small">
            <ArrowBackIosRounded />
          </IconButton>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar style={{
              width: 34, height: 34,
              background: 'linear-gradient(135deg, #15C358 0%, #1a2438 100%)',
              fontSize: 14, fontWeight: 700
            }}>
              {data?.name?.charAt(0).toUpperCase()}
            </Avatar>
            <div>
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{data.name}</h2>
              <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)' }}>{data.clientId.split('_')[0]}</p>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <EditCustomerForm
            customerDetails={data}
            customerId={data.clientId}
            refresh={() => getCustomerDetails(data.clientId)}
          />
          <DeleteButton
            onPositive={() => {
              deleteCustomer(data.clientId);
              getAccountsList().then(d => {
                Object.values(d).forEach(account => {
                  if (account.clientId === data.clientId) deleteAccount(account.accountId);
                });
              });
              navigate("/");
            }}
          />
          <FormControlLabel
            control={
              <IOSSwitch
                name="Status"
                checked={data.isActive}
                onChange={(e) => {
                  setData({ ...data, isActive: e.target.checked });
                  setCustomerStatus(data.clientId, e.target.checked);
                }}
                sx={{ m: 1 }}
              />
            }
            labelPlacement="start"
            label={t('customer_detail.status')}
          />
        </div>
      </div>

      {/* ── Master-Detail Body ── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Left: Loan Accounts List (main content) */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px 120px' }}>

          {/* Mobile-only: customer profile card */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
            <div style={{
              background: 'var(--bg-card)', border: '1px solid var(--border-subtle)',
              borderRadius: 16, padding: '20px 16px', textAlign: 'center', marginBottom: 16
            }}>
              <Avatar style={{
                width: 60, height: 60,
                background: 'linear-gradient(135deg, #15C358 0%, #1a2438 100%)',
                fontSize: 22, fontWeight: 700, margin: '0 auto 10px'
              }}>
                {data?.name?.charAt(0).toUpperCase()}
              </Avatar>
              <h3 style={{ margin: '0 0 4px', fontWeight: 700 }}>{data.name}</h3>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>{data.clientId.split('_')[0]}</p>
              {data.contact && (
                <a href={`tel:${data.contact}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: 'var(--text-primary)', textDecoration: 'none', marginTop: 10, fontWeight: 600, fontSize: 14 }}>
                  <Call style={{ fontSize: 16 }} /> {data.contact}
                </a>
              )}
              {/* Mobile: Share Report button */}
              {data.contact && accountsList.length > 0 && (
                <button
                  onClick={() => {
                    const user = getAuth().currentUser;
                    const url = generateCustomerReport(data.contact, data, accountsList, consoleUrl, user?.displayName || 'Financier');
                    window.open(url, '_blank');
                  }}
                  style={{
                    marginTop: 12, width: '100%', padding: '9px 0', borderRadius: 8,
                    background: '#25D366', color: '#fff', fontWeight: 700, fontSize: 13,
                    border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center',
                  }}
                >
                  <WhatsApp style={{ fontSize: 16 }} /> {t('customer_detail.share_report')}
                </button>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 14, background: 'var(--bg-elevated)', borderRadius: 12, padding: '10px', border: '1px solid var(--border-subtle)' }}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ margin: 0, fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>{t('customer_detail.disbursed')}</p>
                  <p style={{ margin: 0, fontSize: 13, color: 'var(--accent-blue)', fontWeight: 700 }}>₹{totalInvested.toLocaleString('en-IN', { maximumFractionDigits: 3 })}</p>
                </div>
                <div style={{ textAlign: 'center', borderLeft: '1px solid var(--border-subtle)', borderRight: '1px solid var(--border-subtle)' }}>
                  <p style={{ margin: 0, fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>{t('customer_detail.recovered')}</p>
                  <p style={{ margin: 0, fontSize: 13, color: '#0A9E5C', fontWeight: 700 }}>₹{totalPaid.toLocaleString('en-IN', { maximumFractionDigits: 3 })}</p>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ margin: 0, fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>{t('customer_detail.outstanding')}</p>
                  <p style={{ margin: 0, fontSize: 13, color: 'var(--text-primary)', fontWeight: 700 }}>₹{activeBalance.toLocaleString('en-IN', { maximumFractionDigits: 3 })}</p>
                </div>
              </div>
            </div>
          </Box>

          {/* Loan Accounts */}
          <Accounts filterId={clientId} hideSearchBar={true} hideTodayCollectionCard={true} />
        </div>

        {/* Right: Properties Pane — sticky (desktop only) */}
        <Box sx={{
          width: 300, flexShrink: 0,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          overflowY: 'auto',
          borderLeft: '1px solid var(--border-subtle)',
          background: 'var(--bg-card)',
          padding: '24px 20px'
        }}>
          {/* Avatar + Name */}
          <div style={{ textAlign: 'center', marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid var(--border-subtle)' }}>
            <Avatar style={{
              width: 64, height: 64,
              background: 'linear-gradient(135deg, #15C358 0%, #1a2438 100%)',
              fontSize: 24, fontWeight: 700, margin: '0 auto 12px'
            }}>
              {data?.name?.charAt(0).toUpperCase()}
            </Avatar>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>{data.name}</h3>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>{data.clientId.split('_')[0]}</p>
              <IconButton size="small" onClick={() => navigator.clipboard.writeText(data.clientId.split('_')[0])} style={{ padding: 2 }}>
                <ContentCopy style={{ fontSize: 12, color: 'var(--text-muted)' }} />
              </IconButton>
            </div>
          </div>

          {/* Contact */}
          {data.contact && (
            <div style={{ marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid var(--border-subtle)' }}>
              <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>{t('customer_detail.contact')}</p>
              <a href={`tel:${data.contact}`} style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-primary)', textDecoration: 'none', fontWeight: 600, marginBottom: 10, fontSize: 14 }}>
                <Call style={{ fontSize: 16, color: 'var(--text-muted)' }} /> {data.contact}
              </a>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <IconButton href={`sms:${data.contact}`} size="small" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                  <MessageRounded style={{ color: "#f5ae33", fontSize: 18 }} />
                </IconButton>
                <IconButton target="_blank" href={`whatsapp://send?phone=+91${data.contact}&text=Hello ${data.name}`} size="small" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                  <WhatsApp style={{ color: "#25D366", fontSize: 18 }} />
                </IconButton>
              </div>
              {/* Desktop: Share Report button */}
              {accountsList.length > 0 && (
                <button
                  onClick={() => {
                    const user = getAuth().currentUser;
                    const url = generateCustomerReport(data.contact, data, accountsList, consoleUrl, user?.displayName || 'Financier');
                    window.open(url, '_blank');
                  }}
                  style={{
                    marginTop: 14, width: '100%', padding: '9px 0', borderRadius: 8,
                    background: '#25D366', color: '#fff', fontWeight: 700, fontSize: 12,
                    border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: 6
                  }}
                >
                  <WhatsApp style={{ fontSize: 15 }} /> {t('customer_detail.share_report')}
                </button>
              )}
            </div>
          )}

          {/* Alt contact */}
          {data.altContact && (
            <div style={{ marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid var(--border-subtle)' }}>
              <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>{t('customer_detail.alt_contact')}</p>
              <a href={`tel:${data.altContact}`} style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-primary)', textDecoration: 'none', fontWeight: 600, fontSize: 14 }}>
                <Call style={{ fontSize: 16, color: 'var(--text-muted)' }} /> {data.altContact}
              </a>
            </div>
          )}

          {/* Details */}
          <p style={{ margin: '0 0 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>{t('customer_detail.details')}</p>
          {[
            { label: t('customer_detail.surname'), value: data.surName },
            { label: t('customer_detail.town'), value: data.townName },
          ].filter(r => r.value).map(({ label, value }) => (
            <div key={label} style={{ padding: '10px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <p style={{ margin: '0 0 3px', fontSize: 11, color: 'var(--text-muted)' }}>{label}</p>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{value}</p>
            </div>
          ))}

          {/* Analytics */}
          <div style={{ marginTop: 20 }}>
            <p style={{ margin: '0 0 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>{t('customer_detail.analytics')}</p>
            {[
              { label: t('customer_detail.total_disbursed'), value: `₹${totalInvested.toLocaleString('en-IN', { maximumFractionDigits: 3 })}`, color: 'var(--accent-blue)' },
              { label: t('customer_detail.total_recovered'), value: `₹${totalPaid.toLocaleString('en-IN', { maximumFractionDigits: 3 })}`, color: '#0A9E5C' },
              { label: t('customer_detail.outstanding'), value: `₹${activeBalance.toLocaleString('en-IN', { maximumFractionDigits: 3 })}`, color: '#ef4444' },
              { label: t('customer_detail.active_accounts'), value: accountsList.length },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ padding: '10px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                <p style={{ margin: '0 0 3px', fontSize: 11, color: 'var(--text-muted)' }}>{label}</p>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: color || 'var(--text-primary)' }}>{value}</p>
              </div>
            ))}
          </div>
        </Box>
      </div>

      {/* FAB — Add Account */}
      <AddAccountForm
        isFab={true}
        buttonStyle={{ position: "fixed", right: 20, bottom: 80, zIndex: 1000, boxShadow: "0 4px 14px rgba(0,0,0,0.3)" }}
        customer={{ name: data.name, contact: data.contact, clientId: data.clientId }}
        buttonText="Account"
      />
    </div>
  );
}

export default CustomerDetail;
